import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { orderAPI } from '../api';

export default function CheckoutPage() {
  const { cart, fetchCart } = useCart();
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const items = cart.items || [];
  const shipping = cart.total >= 50 ? 0 : 9.99;
  const grandTotal = (cart.total || 0) + shipping;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!address.trim()) { setError('Please enter a shipping address'); return; }
    setLoading(true);
    setError('');
    try {
      await orderAPI.createOrder({ shipping_address: address, notes });
      await fetchCart();
      navigate('/orders');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to place order');
    } finally { setLoading(false); }
  };

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="checkout-page">
      <h1 className="text-2xl font-bold text-gray-900 mb-8" style={{ fontFamily: "'Chivo', sans-serif" }}>Checkout</h1>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded mb-6" data-testid="checkout-error">{error}</div>}
      <div className="grid md:grid-cols-2 gap-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Shipping Address</label>
            <textarea value={address} onChange={e => setAddress(e.target.value)} rows={3} required
              className="w-full border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none" placeholder="Enter your full shipping address"
              data-testid="checkout-address" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Order Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              className="w-full border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none" placeholder="Any special instructions..."
              data-testid="checkout-notes" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 rounded transition-all active:scale-95 disabled:opacity-50"
            data-testid="place-order-btn"
          >
            {loading ? 'Placing Order...' : `Place Order - $${grandTotal.toFixed(2)}`}
          </button>
        </form>

        <div className="bg-white border rounded p-6">
          <h3 className="font-bold text-gray-900 mb-4" style={{ fontFamily: "'Chivo', sans-serif" }}>Order Summary</h3>
          <div className="space-y-3 mb-4">
            {items.map(item => (
              <div key={item.product_id} className="flex justify-between text-sm">
                <span className="text-gray-700">{item.product?.name} x{item.quantity}</span>
                <span className="font-semibold">${item.item_total?.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>${cart.total?.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span className={shipping === 0 ? 'text-green-600' : ''}>{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span></div>
            <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span className="text-[#0F766E]">${grandTotal.toFixed(2)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
